import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import {
    useGetGoodTariffsData,
    useGetMainNumbers,
    useGetPrettyNumbers,
    useGetReservations,
} from '@/api';
import {
    GetMainNumbersParams,
    GetReservationsResponse,
    GoodTariffsResListItem,
    NumbersResponse,
    NumbersSortParams,
} from '@/api/types';
import { NumbersList } from '@/components/AllNumbers/NumbersList/NumbersList.tsx';
import { TariffsList } from '@/components/AllNumbers/TariffsList/TariffsList.tsx';
import { ActiveFiltersList } from '@/components/PrettyNumbers/ActiveFiltersList/ActiveFiltersList.tsx';
import { OpenFiltersBtn } from '@/components/PrettyNumbers/OpenFiltersBtn/OpenFiltersBtn.tsx';
import { FetchError } from '@/components/shared/FetchError/FetchError.tsx';
import { appRoutes } from '@/routes/app-routes.ts';
import { useAuthStore, useChosenNumberStore, useFilterStore, useModalStore } from '@/store';
import { ChosenNumber, DropdownOption, SelectOnChange } from '@/types';

import { Loader } from '../Loader/Loader.tsx';
import { ChosenBlock } from './ChosenBlock/ChosenBlock.tsx';
import { Filter } from './Filter/Filter.tsx';
import cn from './styles.module.scss';

interface IAllNumbersPros {
    hasTitle?: boolean;
    isGoodTariffs?: boolean;
    hasHead?: boolean;
    hasLoadMore?: boolean;
}

const filterOptions = [
    {
        value: 'popular',
        label: 'Популярные',
    },
    {
        value: 'price_asc',
        label: 'Дешевле',
    },
    {
        value: 'price_desc',
        label: 'Дороже',
    },
];

export const AllNumbers = ({
    hasTitle = true,
    isGoodTariffs = false,
    hasHead = true,
    hasLoadMore = true,
}: IAllNumbersPros) => {
    const queryClient = useQueryClient();
    const { chosenNumber, setChosenNumber, isBtnDisabled, setIsBtnDisabled, clearOnUnmount } =
        useChosenNumberStore();
    const {
        currentPage,
        setCurrentPage,
        prevPage,
        setPrevPage,
        searchParamsStr,
        prevSearchParamsStr,
        setPrevSearchParamsStr,
        totalActiveFilters,
        isFiltersOpen,
        setIsFiltersOpen,
    } = useFilterStore();
    const { isAuth } = useAuthStore();
    const { setIsOpen } = useModalStore();

    const { pathname } = useLocation();

    const [numsData, setNumsData] = useState<Omit<NumbersResponse['data'], 'total'>>();
    const [tariffsData, setTariffsData] = useState<GoodTariffsResListItem[]>();
    const [reservationsData, setReservationsData] = useState<GetReservationsResponse['data']>();
    const [currentSort, setCurrentSort] = useState<NumbersSortParams>('popular');
    const prevSort = useRef<NumbersSortParams>('popular');

    const {
        data: mainNumsData,
        isFetching: isMainNumsFetching,
        isError: isMainNumsError,
    } = useGetMainNumbers(currentPage, currentSort, currentPage !== prevPage || currentPage === 0);
    const {
        data: prettyNumsData,
        isFetching: isPrettyNumsFetching,
        isError: isPrettyNumsError,
    } = useGetPrettyNumbers(currentPage, currentSort, searchParamsStr);

    const {
        data: getReservationsData,
        isFetching: isReservationsFetching,
        isError: isReservationsError,
    } = useGetReservations();

    const {
        data: goodTariffsData,
        isFetching: isGoodTariffsFetching,
        isError: isGoodTariffsError,
    } = useGetGoodTariffsData(searchParamsStr);

    const isError =
        isMainNumsError || isPrettyNumsError || isReservationsError || isGoodTariffsError;

    const { number, operator, tariff, region, price } = chosenNumber || {};

    const handleClick = (chosenNumber: Partial<ChosenNumber>) => {
        setChosenNumber(chosenNumber);
        setIsBtnDisabled(false);
    };

    const handleTariffsSortChange = (newValue: DropdownOption<string>) => {
        const value = newValue.value as NumbersSortParams;

        if (value === 'popular') setTariffsData(goodTariffsData?.list);
        if (value === 'price_asc')
            setTariffsData(
                tariffsData?.toSorted((a, b) => a.tariff.publicPrice - b.tariff.publicPrice)
            );
        if (value === 'price_desc')
            setTariffsData(
                tariffsData?.toSorted((a, b) => b.tariff.publicPrice - a.tariff.publicPrice)
            );
    };

    const handleSortChange = (newValue: DropdownOption<string>) => {
        if (currentSort !== newValue.value) {
            prevSort.current = currentSort;
            setPrevPage(0);
            setNumsData({ list: [] });
            setCurrentPage(0);
            setCurrentSort(newValue.value as GetMainNumbersParams['sort']);
        }
    };

    const handleLoadMore = () => {
        if (isAuth) {
            if (!(isMainNumsFetching || isPrettyNumsFetching)) {
                setCurrentPage(currentPage + 1);
            }
        } else {
            setIsOpen(true);
        }
    };

    const handleOpenFilters = () => {
        setIsFiltersOpen(true);
    };

    const ItemsList = isGoodTariffs ? (
        <TariffsList
            isLoading={isGoodTariffsFetching}
            onClick={handleClick}
            list={tariffsData}
            foundAmount={tariffsData?.length}
            tariffsData={goodTariffsData}
        />
    ) : (
        <NumbersList
            isLoading={
                (currentPage === 0 && (isMainNumsFetching || isPrettyNumsFetching)) ||
                isReservationsFetching
            }
            numsData={pathname.includes(appRoutes.myReservations) ? reservationsData : numsData}
            onClick={handleClick}
            foundAmount={reservationsData?.length ?? prettyNumsData?.total}
        />
    );

    const showFilter =
        !pathname.includes(appRoutes.goodTariffs) ||
        (!!goodTariffsData && goodTariffsData.list.length > 0);

    useEffect(() => {
        return () => {
            clearOnUnmount();
            setCurrentPage(0);
            setPrevPage(0);
            queryClient.removeQueries({ queryKey: ['main-numbers'] });
            queryClient.removeQueries({ queryKey: ['pretty-numbers'] });
        };
    }, []);

    useEffect(() => {
        setTariffsData(goodTariffsData?.list);
    }, [goodTariffsData?.list]);

    useEffect(() => {
        setReservationsData(getReservationsData);
    }, [getReservationsData]);

    useEffect(() => {
        const data =
            pathname === appRoutes.mainPage || pathname.includes(appRoutes.myReservations)
                ? mainNumsData
                : prettyNumsData;

        if (
            numsData &&
            data &&
            prevPage !== currentPage &&
            prevSort.current === currentSort &&
            prevSearchParamsStr === searchParamsStr
        ) {
            setPrevPage(currentPage);
            setNumsData({ list: numsData.list.concat(data.list) });
        } else {
            if (
                numsData === undefined ||
                numsData.list.length === 0 ||
                (prevSearchParamsStr === searchParamsStr && prettyNumsData)
            ) {
                setNumsData(data ?? { list: [] });
            }

            if (prevSearchParamsStr !== searchParamsStr && prettyNumsData) {
                setNumsData(data);
            }
        }
        prevSort.current = currentSort;
        setPrevSearchParamsStr();
    }, [mainNumsData, prettyNumsData]);

    return (
        <section className={clsx('all-nums-block', isFiltersOpen && cn.hide)}>
            <div className="all-nums-container">
                {hasHead && (
                    <div
                        className={clsx(
                            cn.allNumsHead,
                            !hasTitle && cn.columnReverse,
                            isError && cn.error
                        )}
                    >
                        {hasTitle ? (
                            <h3 className={cn.numbersTitle}>Все номера</h3>
                        ) : (
                            <ActiveFiltersList />
                        )}
                        <div className={clsx(cn.filterSortContainer, !showFilter && cn.hide)}>
                            {!hasTitle && (
                                <OpenFiltersBtn
                                    onClick={handleOpenFilters}
                                    totalActiveFilters={totalActiveFilters}
                                />
                            )}
                            {showFilter && (
                                <Filter
                                    options={filterOptions}
                                    onChange={
                                        (isGoodTariffs
                                            ? handleTariffsSortChange
                                            : handleSortChange) as SelectOnChange
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}

                {isError ? <FetchError /> : ItemsList}

                {!isError && !isGoodTariffs && hasLoadMore && (
                    <div onClick={handleLoadMore} className={cn.loadMore}>
                        {isMainNumsFetching || isPrettyNumsFetching ? (
                            <Loader mini />
                        ) : numsData && numsData.list.length > 0 ? (
                            'Загрузить еще'
                        ) : null}
                    </div>
                )}
            </div>
            <ChosenBlock
                isBtnDisabled={isBtnDisabled}
                number={number}
                operator={operator}
                tariff={tariff}
                price={price}
                region={region}
            />
        </section>
    );
};
